// ShortsFeed — the phone-shaped, snap-scrolling reel rail on the Classroom hub
// (desktop only; the horizontal "Short Format Content" rail stays on mobile).
//
// Why it is built this way (decided 2026-09-04/05 with Master):
//   * Cards show the short's own YouTube thumbnail (current skin). If a
//     current-skin MP4 ever lands on the CDN (`mp4` in src/data/shorts.ts) the
//     card autoplays it muted as a preview instead; the 16 old TikTok-format
//     renders are retired (`legacyMp4`) and never shown.
//   * Clicking anywhere on a card starts a "session": the card swaps to the
//     YouTube Shorts player (IFrame Player API) and plays. From then on the
//     rail behaves like a reel — scrolling pauses the card that leaves and
//     mounts + plays the card that arrives (Master, 2026-09-05: "pause the
//     previous ones playing and play the next"). Before the first click nothing
//     plays with sound (browsers block it, and YouTube wouldn't credit it).
//   * Captions are switched off on the player (the videos carry their own
//     burned-in captions — Master, 2026-09-05).
//   * Trade-off, stated plainly: YouTube tends to discount plays an embed
//     starts itself, so a viewer's own press of YouTube's play button is the
//     surer counted view. Master chose the one-click / reel behaviour.
//   * "TikTok" is a click-through to the post (or the account until the post
//     URL is filled in src/data/shorts.ts) — click-throughs are what TikTok counts.
//   * Until the channel allows embedding (SHORTS_EMBED_ALLOWED in
//     src/data/shorts.ts) a click opens the short on youtube.com in a new tab.
//   * The feed is endless: it cycles the catalogue with a Follow card between
//     loops. Cards far from the viewport drop their player so a long scroll
//     session doesn't accumulate iframes.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Play, ExternalLink } from "lucide-react";
import { SHORTS_EMBED_ALLOWED, type ShortClip } from "@/data/shorts";
import { liveSocialLinks } from "@/config/socialLinks";
import { loadYouTubeApi, disableCaptions } from "@/lib/youtubePlayer";

const sans = "'Instrument Sans', system-ui, -apple-system, sans-serif";
const serif = "'Instrument Serif', Georgia, serif";
const TIKTOK_ACCOUNT = "https://www.tiktok.com/@americanhospitalprices";
const PAGE = 4;             // cards appended per sentinel hit
const NEAR_MARGIN = "1400px"; // keep a player mounted within this distance of the viewport
const GAP = 16;             // breathing room above the rail and above the bottom bar

/** YouTube's vertical `oar2.jpg` is missing for some shorts; `hqdefault.jpg` always exists. */
export function posterFallback(e: React.SyntheticEvent<HTMLImageElement>) {
  const el = e.currentTarget;
  if (el.dataset.fallback) return;
  el.dataset.fallback = "1";
  el.src = el.src.replace(/\/[a-z0-9]+\.jpg$/i, "/hqdefault.jpg");
}

export function youtubeIdFrom(url: string | null | undefined): string | null {
  if (!url) return null;
  const m = url.match(/(?:shorts\/|watch\?v=|youtu\.be\/|embed\/)([A-Za-z0-9_-]{6,})/);
  return m ? m[1] : null;
}

/**
 * The page has a fixed top chrome (announcement strip + header + flag stripe)
 * and a fixed bottom app bar (AppFooter, ~70px). Measure both so the sticky
 * rail sits exactly between them instead of running behind the bar.
 */
function useChromeInsets() {
  const [insets, setInsets] = useState({ top: 106, bottom: 70 });
  useEffect(() => {
    const measure = () => {
      const header = document.querySelector("header");
      const hb = header ? header.getBoundingClientRect().bottom : 106;
      const bar = document.querySelector('nav[class*="fixed"][class*="bottom-0"]') as HTMLElement | null;
      const bh = bar ? bar.getBoundingClientRect().height : 70;
      setInsets({ top: Math.round(hb + 6), bottom: Math.round(bh) });
    };
    measure();
    window.addEventListener("resize", measure);
    const t = setTimeout(measure, 800); // fonts / late-mounting bar
    return () => { window.removeEventListener("resize", measure); clearTimeout(t); };
  }, []);
  return insets;
}

type FeedItem =
  | { kind: "clip"; key: string; clip: ShortClip; loop: number }
  | { kind: "follow"; key: string; loop: number };

interface ShortsFeedProps {
  clips: ShortClip[];
  dark: boolean;
  /** A viewer opened a clip on a platform ("youtube" = started the player here; "tiktok" = click-through). */
  onOpen?: (platform: "youtube" | "tiktok", clip: ShortClip) => void;
  /** The embedded YouTube player actually reached the PLAYING state for this clip. */
  onPlay?: (clip: ShortClip) => void;
}

export function ShortsFeed({ clips, dark, onOpen, onPlay }: ShortsFeedProps) {
  const railRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [count, setCount] = useState(Math.min(PAGE + 2, clips.length + 1));
  // Set by the first click on any card; from then on the rail plays the card
  // in view and pauses the one that left.
  const [sessionPlaying, setSessionPlaying] = useState(false);

  // Endless item list: clip, clip, …, follow-card, clip, clip … (per loop).
  const items = useMemo<FeedItem[]>(() => {
    const out: FeedItem[] = [];
    if (!clips.length) return out;
    const perLoop = clips.length + 1;
    for (let i = 0; i < count; i++) {
      const loop = Math.floor(i / perLoop);
      const pos = i % perLoop;
      if (pos === clips.length) out.push({ kind: "follow", key: `follow-${loop}`, loop });
      else out.push({ kind: "clip", key: `${clips[pos].id}-${loop}`, clip: clips[pos], loop });
    }
    return out;
  }, [clips, count]);

  // Append more when the bottom sentinel comes into view (root = the rail itself).
  useEffect(() => {
    const root = railRef.current;
    const el = sentinelRef.current;
    if (!root || !el) return;
    const io = new IntersectionObserver(
      (entries) => { if (entries.some((e) => e.isIntersecting)) setCount((c) => c + PAGE); },
      { root, rootMargin: "600px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const rule = dark ? "#25364f" : "#ddd5c4";
  const muted = dark ? "#9fb0c8" : "#6b6250";
  const insets = useChromeInsets();
  const top = insets.top + GAP;

  // Card = min(rail width, rail height × 9/16): as wide as the rail allows,
  // never taller than one rail screen. Kept in a CSS var by a ResizeObserver.
  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return;
    const ro = new ResizeObserver(() => {
      rail.style.setProperty("--card-h", `${Math.max(0, rail.clientHeight - 20)}px`);
    });
    ro.observe(rail);
    return () => ro.disconnect();
  }, []);

  return (
    <div style={{ position: "sticky", top, height: `calc(100vh - ${top + insets.bottom + GAP}px)`, display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, padding: "0 4px 8px" }}>
        <div style={{ fontFamily: serif, fontSize: 22, color: dark ? "#e9eef6" : "#1f3a5f", lineHeight: 1.1 }}>Shorts</div>
        <div style={{ fontFamily: sans, fontSize: 12, color: muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          60-second price checks · scroll like a phone
        </div>
      </div>

      <div
        ref={railRef}
        style={{
          flex: 1, minHeight: 0, overflowY: "auto", scrollSnapType: "y mandatory",
          scrollbarWidth: "none", borderRadius: 18,
          border: `1.5px solid ${rule}`, background: dark ? "#0b1526" : "#0f1a2e",
          boxShadow: dark ? "0 12px 32px rgba(0,0,0,.45)" : "0 12px 32px rgba(15,26,46,.28)",
        }}
      >
        {items.map((it) =>
          it.kind === "clip" ? (
            <FeedCard
              key={it.key}
              clip={it.clip}
              root={railRef}
              sessionPlaying={sessionPlaying}
              onStartSession={() => setSessionPlaying(true)}
              onOpen={onOpen}
              onPlay={onPlay}
            />
          ) : (
            <FollowCard key={it.key} />
          ),
        )}
        <div ref={sentinelRef} style={{ height: 1 }} aria-hidden />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function FeedCard({ clip, root, sessionPlaying, onStartSession, onOpen, onPlay }: {
  clip: ShortClip;
  root: React.RefObject<HTMLDivElement>;
  sessionPlaying: boolean;
  onStartSession: () => void;
  onOpen?: (platform: "youtube" | "tiktok", clip: ShortClip) => void;
  onPlay?: (clip: ShortClip) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const hostRef = useRef<HTMLDivElement>(null);   // the YT.Player mounts inside this
  const playerRef = useRef<any>(null);
  const activeRef = useRef(false);
  const playedRef = useRef(false);
  const [near, setNear] = useState(false);     // within NEAR_MARGIN → keep media mounted
  const [active, setActive] = useState(false); // mostly visible → this is "the" card
  const [mode, setMode] = useState<"preview" | "youtube">("preview");
  const [hover, setHover] = useState(false);
  const [embedError, setEmbedError] = useState(false);
  const ytId = youtubeIdFrom(clip.youtubeUrl);
  const hasVideo = !!clip.mp4;
  const canEmbed = SHORTS_EMBED_ALLOWED && !!ytId;
  activeRef.current = active;

  useEffect(() => {
    const el = ref.current;
    const r = root.current;
    if (!el || !r) return;
    const nearIo = new IntersectionObserver(
      (es) => setNear(es.some((e) => e.isIntersecting)),
      { root: r, rootMargin: `${NEAR_MARGIN} 0px` },
    );
    const activeIo = new IntersectionObserver(
      (es) => setActive(es.some((e) => e.isIntersecting)),
      { root: r, threshold: 0.6 },
    );
    nearIo.observe(el);
    activeIo.observe(el);
    return () => { nearIo.disconnect(); activeIo.disconnect(); };
  }, [root]);

  // Muted MP4 preview (only when a current-skin MP4 exists): play while this
  // card is the one in view, pause otherwise.
  useEffect(() => {
    const v = videoRef.current;
    if (!v || mode !== "preview") return;
    if (active) { v.play().catch(() => { /* autoplay blocked — poster stays */ }); }
    else { v.pause(); }
  }, [active, mode, near]);

  // Reel behaviour: once a session is playing, the card that scrolls into view
  // becomes the player.
  useEffect(() => {
    if (active && sessionPlaying && canEmbed && mode === "preview") setMode("youtube");
  }, [active, sessionPlaying, canEmbed, mode]);

  // Leaving the neighbourhood tears the player down (scrolling back rebuilds it).
  useEffect(() => { if (!near && mode === "youtube") { setMode("preview"); setEmbedError(false); } }, [near, mode]);

  // Create / destroy the YouTube player when the card enters / leaves "youtube" mode.
  useEffect(() => {
    if (mode !== "youtube" || !ytId) return;
    let cancelled = false;
    let player: any = null;
    const host = hostRef.current;
    loadYouTubeApi().then((YT) => {
      if (cancelled || !host) return;
      const target = document.createElement("div");
      host.appendChild(target);
      player = new YT.Player(target, {
        videoId: ytId,
        host: "https://www.youtube-nocookie.com",
        width: "100%",
        height: "100%",
        playerVars: { autoplay: 1, playsinline: 1, rel: 0, modestbranding: 1, cc_load_policy: 3, origin: window.location.origin },
        events: {
          onReady: (e: any) => {
            playerRef.current = e.target;
            disableCaptions(e.target);
            try { activeRef.current ? e.target.playVideo() : e.target.pauseVideo(); } catch { /* noop */ }
          },
          onStateChange: (e: any) => {
            // 1 = PLAYING (YT.PlayerState.PLAYING)
            if (e.data === 1 && !playedRef.current) { playedRef.current = true; onPlay?.(clip); }
          },
          onError: () => setEmbedError(true),
        },
      });
    });
    return () => {
      cancelled = true;
      try { player?.destroy(); } catch { /* noop */ }
      playerRef.current = null;
      if (host) host.innerHTML = "";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, ytId]);

  // Scroll away → pause; scroll back → resume.
  useEffect(() => {
    const p = playerRef.current;
    if (!p || mode !== "youtube") return;
    try { active ? p.playVideo() : p.pauseVideo(); } catch { /* not ready yet — onReady handles it */ }
  }, [active, mode]);

  const openYouTube = useCallback(() => {
    onOpen?.("youtube", clip);
    if (canEmbed) {
      onStartSession();
      setMode("youtube");
    } else {
      window.open(clip.youtubeUrl, "_blank", "noopener,noreferrer");
    }
  }, [canEmbed, clip, onOpen, onStartSession]);

  const tiktokHref = clip.tiktokUrl || TIKTOK_ACCOUNT;
  const isPreview = mode === "preview";

  return (
    <div ref={ref} style={{ height: "100%", scrollSnapAlign: "start", scrollSnapStop: "always", display: "flex", alignItems: "center", justifyContent: "center", padding: 10, boxSizing: "border-box" }}>
      <div
        role={isPreview ? "button" : undefined}
        tabIndex={isPreview ? 0 : undefined}
        aria-label={isPreview ? `Play: ${clip.title}` : undefined}
        onClick={isPreview ? openYouTube : undefined}
        onKeyDown={isPreview ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openYouTube(); } } : undefined}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        data-mode={mode}
        style={{ position: "relative", width: "min(100%, calc(var(--card-h, 640px) * 9 / 16))", aspectRatio: "9 / 16", borderRadius: 14, overflow: "hidden", background: "#050b16", cursor: isPreview ? "pointer" : undefined }}
      >
        {mode === "youtube" && ytId ? (
          <>
            {/* YT.Player host — the API replaces the child div with the iframe */}
            <div ref={hostRef} className="yt-host" style={{ position: "absolute", inset: 0 }} />
            {embedError && (
              <a
                href={clip.youtubeUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => onOpen?.("youtube", clip)}
                style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, textDecoration: "none", color: "#f2ecdd", background: "rgba(5,11,22,.92)", padding: 24, textAlign: "center" }}
              >
                <span style={{ fontFamily: sans, fontSize: 13, color: "#cdd6e4" }}>This short can't play here yet.</span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontFamily: sans, fontSize: 13, fontWeight: 700, padding: "9px 14px", borderRadius: 9, background: "#FF0000", color: "#fff" }}>
                  <Play size={14} fill="currentColor" /> Watch on YouTube <ExternalLink size={12} />
                </span>
              </a>
            )}
          </>
        ) : (
          <>
            {near && hasVideo ? (
              <video
                ref={videoRef}
                src={clip.mp4!}
                poster={clip.poster}
                muted
                loop
                playsInline
                preload={active ? "auto" : "metadata"}
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", display: "block" }}
              />
            ) : near ? (
              <img
                src={clip.poster}
                alt=""
                loading="lazy"
                onError={posterFallback}
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", display: "block" }}
              />
            ) : (
              <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg,#132340,#050b16)" }} />
            )}
            {/* Centred play glyph — the whole card is the click target */}
            <div
              aria-hidden
              style={{
                position: "absolute", top: "50%", left: "50%", transform: `translate(-50%, -50%) scale(${hover ? 1.08 : 1})`,
                width: 72, height: 72, borderRadius: "50%",
                background: hover ? "rgba(255,0,0,.95)" : "rgba(15,26,46,.72)",
                border: "2px solid rgba(255,255,255,.85)", boxShadow: "0 8px 24px rgba(0,0,0,.45)",
                display: "flex", alignItems: "center", justifyContent: "center",
                transition: "transform .15s ease, background .15s ease",
              }}
            >
              <Play size={30} color="#fff" fill="#fff" style={{ marginLeft: 4 }} />
            </div>
            {/* Title + payoff */}
            <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, padding: "70px 14px 14px", background: "linear-gradient(180deg, rgba(5,11,22,0) 0%, rgba(5,11,22,.88) 70%)", color: "#f2ecdd" }}>
              <div style={{ fontFamily: sans, fontSize: 11, fontWeight: 700, letterSpacing: ".08em", color: "#C5A55A" }}>{clip.payoff}</div>
              <div style={{ fontFamily: serif, fontSize: 19, lineHeight: 1.15, marginTop: 4 }}>{clip.title}</div>
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); openYouTube(); }}
                  style={{ flex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, fontFamily: sans, fontSize: 12.5, fontWeight: 700, padding: "9px 10px", borderRadius: 9, border: 0, cursor: "pointer", background: "#FF0000", color: "#fff" }}
                >
                  <Play size={14} fill="currentColor" /> Watch on YouTube
                  {!canEmbed && <ExternalLink size={12} />}
                </button>
                <a
                  href={tiktokHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => { e.stopPropagation(); onOpen?.("tiktok", clip); }}
                  title={clip.tiktokUrl ? "Open this clip on TikTok" : "Our TikTok — this clip's direct post link coming soon"}
                  style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, fontFamily: sans, fontSize: 12.5, fontWeight: 700, padding: "9px 12px", borderRadius: 9, textDecoration: "none", background: "#fff", color: "#000" }}
                >
                  TikTok <ExternalLink size={12} />
                </a>
              </div>
            </div>
            {/* Card hint: muted preview when we have the MP4, otherwise the code */}
            <div style={{ position: "absolute", top: 10, left: 10, fontFamily: sans, fontSize: 10.5, fontWeight: 700, letterSpacing: ".06em", background: "rgba(15,26,46,.75)", color: "#cdd6e4", padding: "3px 8px", borderRadius: 6 }}>
              {hasVideo ? "MUTED PREVIEW" : `CPT ${clip.code}`}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function FollowCard() {
  const socials = liveSocialLinks();
  return (
    <div style={{ height: "100%", scrollSnapAlign: "start", scrollSnapStop: "always", display: "flex", alignItems: "center", justifyContent: "center", padding: 10, boxSizing: "border-box" }}>
      <div style={{ width: "min(100%, calc(var(--card-h, 640px) * 9 / 16))", aspectRatio: "9 / 16", borderRadius: 14, background: "linear-gradient(180deg,#1f3a5f,#0f1a2e)", color: "#f2ecdd", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", padding: 24, boxSizing: "border-box" }}>
        <div style={{ fontFamily: sans, fontSize: 11, fontWeight: 700, letterSpacing: ".1em", color: "#C5A55A" }}>THAT'S THE LOOP</div>
        <div style={{ fontFamily: serif, fontSize: 24, lineHeight: 1.15, marginTop: 8 }}>New price checks drop weekly.</div>
        <div style={{ fontFamily: sans, fontSize: 13, color: "#cdd6e4", marginTop: 8, maxWidth: "26ch" }}>Follow so the next one finds you. Keep scrolling to watch again.</div>
        <div style={{ display: "flex", gap: 10, marginTop: 18, flexWrap: "wrap", justifyContent: "center" }}>
          {socials.map((s) => (
            <a
              key={s.id}
              href={s.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${s.label} — ${s.handle}`}
              title={`${s.label} · ${s.handle}`}
              style={{ width: 44, height: 44, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid rgba(197,165,90,.45)", color: "#f2ecdd" }}
            >
              <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true"><path d={s.path} /></svg>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

export default ShortsFeed;
